import React from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connect } from "react-redux";
import { updateSelectedCuisines } from "../../actions/restaurants";
import { setMinLeafCount, setVerificationTier } from "../../actions/filters";
import useEvent from "../../hooks/useEvent";
import "./ActiveFilters.css";

/**
 * ActiveFilters - Displays current search filters as dismissible chips
 * Shows: cuisines, keyword, leaf count, verification tier
 * (Location is now in UnifiedSearchBar)
 */
const ActiveFilters = ({
  // Cuisines
  selectedCuisines,
  updateSelectedCuisines,
  // Leaf filters
  minLeafCount,
  setMinLeafCount,
  verificationTier,
  setVerificationTier,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { event } = useEvent();

  // Get keyword and list name from URL (source of truth)
  const searchParams = new URLSearchParams(location.search);
  const keywordFromUrl = searchParams.get("keywords");
  const listName = searchParams.get("listName");

  // Get array of selected cuisine names
  const cuisineList = Object.values(selectedCuisines || {});

  // Check if we have any active filters to show
  const hasCuisines = cuisineList.length > 0;
  const hasKeyword = keywordFromUrl && keywordFromUrl.trim() !== "";
  const hasList = !!listName;
  const hasLeafFilter = minLeafCount !== null;
  const hasVerificationFilter = verificationTier !== null;

  // Don't show leaf/verification filters for events
  const showLeafFilters = !event;

  const hasAnyFilters = hasCuisines || hasKeyword || hasList ||
    (showLeafFilters && (hasLeafFilter || hasVerificationFilter));

  if (!hasAnyFilters) {
    return null;
  }

  // Helper: update a URL param and navigate
  const updateUrlParam = (key, value) => {
    const params = new URLSearchParams(location.search);
    if (value !== null && value !== undefined && value !== "") {
      params.set(key, value.toString());
    } else {
      params.delete(key);
    }
    navigate(`?${params.toString()}`);
  };

  // Remove a specific cuisine
  const removeCuisine = (cuisineToRemove) => {
    const newCuisines = { ...selectedCuisines };
    for (const key in newCuisines) {
      if (newCuisines[key] === cuisineToRemove) {
        delete newCuisines[key];
        break;
      }
    }
    updateSelectedCuisines(newCuisines);
    // Update URL
    const remaining = Object.values(newCuisines).filter(Boolean).join(",");
    updateUrlParam("cuisine", remaining || null);
  };

  // Clear keyword (URL is the source of truth)
  const clearKeyword = () => {
    const params = new URLSearchParams(location.search);
    params.delete("keywords");
    navigate(`?${params.toString()}`);
  };

  // Leave list — navigate to home with only location params
  const clearList = () => {
    const params = new URLSearchParams(location.search);
    const locationParams = new URLSearchParams();
    for (const key of ["lat", "lng", "radius", "zoom"]) {
      if (params.has(key)) locationParams.set(key, params.get(key));
    }
    navigate(`/?${locationParams.toString()}`);
  };

  // Clear leaf filter
  const clearLeafFilter = () => {
    setMinLeafCount(null);
    updateUrlParam("minLeaf", null);
  };

  // Clear verification filter
  const clearVerificationFilter = () => {
    setVerificationTier(null);
    updateUrlParam("tier", null);
  };

  // Get leaf filter display text
  const getLeafDisplay = () => {
    if (minLeafCount === 2) return "2+ Leaves";
    if (minLeafCount === 3) return "3 Leaves";
    return null;
  };

  // Get verification filter display text
  const getVerificationDisplay = () => {
    if (verificationTier === "guaranteed") return "Confirmed";
    if (verificationTier === "verified") return "Verified";
    return null;
  };

  return (
    <div className="active-filters">
      {/* List name chip */}
      {hasList && (
        <button
          className="active-filter-chip active-filter-list"
          onClick={clearList}
          title="Leave list"
        >
          <i className="fas fa-list" />
          <span>{listName}</span>
          <i className="fas fa-times" />
        </button>
      )}

      {/* Cuisine chips */}
      {cuisineList.map((cuisine) => (
        <button
          key={cuisine}
          className="active-filter-chip active-filter-cuisine"
          onClick={() => removeCuisine(cuisine)}
          title={`Remove ${cuisine} filter`}
        >
          <span>{cuisine}</span>
          <i className="fas fa-times" />
        </button>
      ))}

      {/* Keyword chip */}
      {hasKeyword && (
        <button
          className="active-filter-chip active-filter-keyword"
          onClick={clearKeyword}
          title="Clear keyword search"
        >
          <i className="fas fa-search" />
          <span>"{keywordFromUrl}"</span>
          <i className="fas fa-times" />
        </button>
      )}

      {/* Leaf filter chip */}
      {showLeafFilters && hasLeafFilter && (
        <button
          className="active-filter-chip active-filter-leaf"
          onClick={clearLeafFilter}
          title="Clear leaf filter"
        >
          <i className="fas fa-leaf" />
          <span>{getLeafDisplay()}</span>
          <i className="fas fa-times" />
        </button>
      )}

      {/* Verification filter chip */}
      {showLeafFilters && hasVerificationFilter && (
        <button
          className="active-filter-chip active-filter-verification"
          onClick={clearVerificationFilter}
          title="Clear verification filter"
        >
          <i className={verificationTier === "verified" ? "fas fa-star" : "fas fa-check"} />
          <span>{getVerificationDisplay()}</span>
          <i className="fas fa-times" />
        </button>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  selectedCuisines: state.selectedCuisines,
  minLeafCount: state.filters.minLeafCount,
  verificationTier: state.filters.verificationTier,
});

export default connect(mapStateToProps, {
  updateSelectedCuisines,
  setMinLeafCount,
  setVerificationTier,
})(ActiveFilters);
